import React, {useCallback, useEffect, useRef, useState} from 'react';
import cn from 'classnames';

import PaginationChevronLeftIcon from 'shared/ui/icons/PaginationChevronLeftIcon';
import useBreakpoint from 'shared/lib/hooks/useBreakpoint';

import {t} from '@lingui/core/macro';

import styles from './TariffsSlider.module.scss';

interface ITariffsSliderProps {
    children: React.ReactNode;
}

const SCROLL_EPSILON = 4;
const TARIFFS_PER_PAGE = 4;
const MOBILE_TARIFFS_PER_PAGE = 1;

const TariffsSlider: React.FC<ITariffsSliderProps> = ({children}) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const breakpoint = useBreakpoint();
    const [isScrollable, setIsScrollable] = useState(false);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const slides = React.Children.toArray(children);
    const perPage = breakpoint === 'mini' ? MOBILE_TARIFFS_PER_PAGE : TARIFFS_PER_PAGE;

    const pages: React.ReactNode[][] = [];

    for (let index = 0; index < slides.length; index += perPage) {
        pages.push(slides.slice(index, index + perPage));
    }

    const syncScrollState = useCallback(() => {
        const viewport = viewportRef.current;

        if (!viewport) {
            return;
        }

        const maxScroll = viewport.scrollWidth - viewport.clientWidth;

        setIsScrollable(maxScroll > SCROLL_EPSILON);
        setCanScrollPrev(viewport.scrollLeft > SCROLL_EPSILON);
        setCanScrollNext(viewport.scrollLeft < maxScroll - SCROLL_EPSILON);
    }, []);

    useEffect(() => {
        syncScrollState();
        window.addEventListener('resize', syncScrollState);

        return () => {
            window.removeEventListener('resize', syncScrollState);
        };
    }, [syncScrollState, pages.length]);

    const scrollByPage = (direction: number) => {
        const viewport = viewportRef.current;

        if (!viewport) {
            return;
        }

        viewport.scrollBy({left: viewport.clientWidth * direction, behavior: 'smooth'});
    };

    return (
        <div className={styles.slider}>
            {isScrollable && (
                <button
                    type="button"
                    className={cn(styles.nav, styles.prev)}
                    onClick={() => scrollByPage(-1)}
                    disabled={!canScrollPrev}
                    aria-label={t`Предыдущие тарифы`}
                >
                    <PaginationChevronLeftIcon/>
                </button>
            )}

            <div
                ref={viewportRef}
                className={styles.viewport}
                onScroll={syncScrollState}
            >
                {pages.map((page, pageIndex) => (
                    <div key={pageIndex} className={styles.page}>
                        {page.map((slide, slideIndex) => (
                            <div key={slideIndex} className={styles.slide}>
                                {slide}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {isScrollable && (
                <button
                    type="button"
                    className={cn(styles.nav, styles.next)}
                    onClick={() => scrollByPage(1)}
                    disabled={!canScrollNext}
                    aria-label={t`Следующие тарифы`}
                >
                    <PaginationChevronLeftIcon/>
                </button>
            )}
        </div>
    );
};

export default TariffsSlider;
